import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  setDoc,
  arrayUnion
} from "firebase/firestore";

const normalizeMonthlyRecurrence = (recurrence) => ({
  frequency: "monthly",
  interval: Math.max(1, Number(recurrence?.interval) || 1),
  createTiming: "periodEnd"
});

export const DEFAULT_PROCESS_PROJECT_ID = "monthly-processes";

export const ensureDefaultProcessProject = async () => {
  await setDoc(doc(db, "processProjects", DEFAULT_PROCESS_PROJECT_ID), {
    name: "Aylık Süreçler",
    slug: DEFAULT_PROCESS_PROJECT_ID,
    type: "kanban",
    isActive: true,
    order: 1,
    isSystemDefault: true,
    updatedAt: serverTimestamp()
  }, { merge: true });
};

export const getProcessProjects = async () => {
  try {
    await ensureDefaultProcessProject();
  } catch (error) {
    // Görüntüleyici kullanıcıların süreç projelerini listeleyebilmesi için
    // varsayılan proje oluşturma/güncelleme yetkisi yoksa listelemeye devam edilir.
  }

  const q = query(collection(db, "processProjects"), orderBy("order", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .filter((item) => item.isActive !== false);
};

export const createProcessProject = async ({ name, createdByUid = "", createdByEmail = "" }) => {
  const safeName = String(name || "").trim();

  if (!safeName) {
    throw new Error("Süreç projesi adı zorunludur.");
  }

  return addDoc(collection(db, "processProjects"), {
    name: safeName,
    slug: safeName
      .toLocaleLowerCase("tr")
      .replace(/ı/g, "i")
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, ""),
    type: "kanban",
    isActive: true,
    order: Date.now(),
    createdByUid,
    createdByEmail,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateProcessProject = async ({ projectId, data }) => {
  if (!projectId) {
    throw new Error("Süreç projesi ID bulunamadı.");
  }

  return updateDoc(doc(db, "processProjects", projectId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const getProcessTemplates = async (processProjectId = DEFAULT_PROCESS_PROJECT_ID) => {
  const q = query(collection(db, "processTemplates"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .filter((item) =>
      processProjectId === DEFAULT_PROCESS_PROJECT_ID
        ? !item.processProjectId || item.processProjectId === processProjectId
        : item.processProjectId === processProjectId
    );
};

export const createProcessTemplate = async ({
  name,
  steps,
  recurrence,
  processProjectId = DEFAULT_PROCESS_PROJECT_ID
}) => {
  return addDoc(collection(db, "processTemplates"), {
    name,
    steps,
    recurrence: normalizeMonthlyRecurrence(recurrence),
    processProjectId,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateProcessTemplate = async ({ templateId, name, steps, recurrence }) => {
  if (!templateId) {
    throw new Error("Şablon ID bulunamadı.");
  }

  return updateDoc(doc(db, "processTemplates", templateId), {
    name,
    steps,
    recurrence: normalizeMonthlyRecurrence(recurrence),
    updatedAt: serverTimestamp()
  });
};

export const deleteProcessTemplate = async (templateId) => {
  if (!templateId) {
    throw new Error("Şablon ID bulunamadı.");
  }

  return deleteDoc(doc(db, "processTemplates", templateId));
};

export const getMonthlyProcesses = async (processProjectId = DEFAULT_PROCESS_PROJECT_ID) => {
  const q = query(collection(db, "monthlyProcesses"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .filter((item) =>
      processProjectId === DEFAULT_PROCESS_PROJECT_ID
        ? !item.processProjectId || item.processProjectId === processProjectId
        : item.processProjectId === processProjectId
    );
};

export const getProcessStickyNotes = async (processProjectId) => {
  if (!processProjectId) return [];

  const q = query(
    collection(db, "processStickyNotes"),
    where("processProjectId", "==", processProjectId)
  );
  const snapshot = await getDocs(q);
  const getTime = (value) => {
    if (value?.toMillis) return value.toMillis();
    const parsed = new Date(value || 0).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  return snapshot.docs
    .map((docItem) => ({ id: docItem.id, ...docItem.data() }))
    .sort((first, second) => getTime(second.updatedAt || second.createdAt) - getTime(first.updatedAt || first.createdAt));
};

export const createProcessStickyNote = async ({
  processProjectId,
  text,
  mentionAll = false,
  mentions = [],
  rawMentions = [],
  createdByUid = "",
  createdByEmail = "",
  createdByName = ""
}) => addDoc(collection(db, "processStickyNotes"), {
  processProjectId,
  text: String(text || "").trim(),
  completed: false,
  archived: false,
  mentionAll,
  mentions,
  rawMentions,
  createdByUid,
  createdByEmail,
  createdByName,
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp()
});

export const updateProcessStickyNote = async ({ noteId, data }) => {
  if (!noteId) throw new Error("Yapışkan not ID bulunamadı.");

  return updateDoc(doc(db, "processStickyNotes", noteId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteProcessStickyNote = async (noteId) => {
  if (!noteId) throw new Error("Yapışkan not ID bulunamadı.");
  return deleteDoc(doc(db, "processStickyNotes", noteId));
};

export const createMonthlyProcess = async ({
  companyName,
  companyShortName = "",
  title = "",
  month,
  scheduleType = "month",
  startDate = "",
  endDate = "",
  template,
  owner,
  processProjectId = DEFAULT_PROCESS_PROJECT_ID,
  note = "",
  source = "",
  sourceId = "",
  priority = "",
  dueDate = "",
  extraFields = {}
}) => {
  const steps = (template.steps || []).map((step, index) => ({
    order: step.order || index + 1,
    groupTitle: step.groupTitle || "",
    title: step.title,
    isRequired: step.isRequired !== false,
    completed: false,
    completedAt: null,
    completedBy: ""
  }));

  return addDoc(collection(db, "monthlyProcesses"), {
    companyName: companyName || "",
    companyShortName: companyShortName || "",
    title: title || template.name || "",
    month: month || "",
    scheduleType: scheduleType || "month",
    startDate: startDate || "",
    endDate: endDate || startDate || "",
    owner: owner || "Atanmamış",
    autoRepeat: template.autoRepeatDefault || false,
    recurrence: normalizeMonthlyRecurrence(template.recurrence),
    note: note || "",
    notes: [],
    source: source || "",
    sourceId: sourceId || "",
    priority: priority || "",
    dueDate: dueDate || "",
    templateId: template.id,
    templateName: template.name,
    processProjectId,
    status: "active",
    completionRate: 0,
    steps,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...extraFields
  });
};

export const updateMonthlyProcessSteps = async ({
  processId,
  steps,
  userEmail,
  status
}) => {
  const completedCount = steps.filter((step) => step.completed).length;
  const completionRate = steps.length ? Math.round((completedCount / steps.length) * 100) : 0;

  await updateDoc(doc(db, "monthlyProcesses", processId), {
    steps,
    completionRate,
    status: status || "active",
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const updateMonthlyProcessDetails = async ({
  processId,
  data,
  userEmail = ""
}) => {
  if (!processId) {
    throw new Error("Görev ID bulunamadı.");
  }

  return updateDoc(doc(db, "monthlyProcesses", processId), {
    ...data,
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const updateMonthlyProcessStepProgress = async ({
  processId,
  steps,
  userEmail,
  status,
  extraFields = {}
}) => {
  const completedCount = steps.filter((step) => step.completed).length;
  const completionRate = steps.length ? Math.round((completedCount / steps.length) * 100) : 0;
  const stepProgress = steps.reduce((acc, step, index) => {
    acc[String(index)] = {
      completed: Boolean(step.completed),
      completedAt: step.completedAt || null,
      completedBy: step.completedBy || ""
    };

    return acc;
  }, {});

  await updateDoc(doc(db, "monthlyProcesses", processId), {
    stepProgress,
    completionRate,
    status: status || "active",
    ...extraFields,
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const updateMonthlyProcessExemptionDayReportStatus = async ({
  processId,
  delivered,
  userEmail
}) => {
  await updateDoc(doc(db, "monthlyProcesses", processId), {
    exemptionDayReportDelivered: Boolean(delivered),
    exemptionDayReportAsked: true,
    exemptionDayReportUpdatedAt: serverTimestamp(),
    exemptionDayReportUpdatedBy: userEmail || "",
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const recordMonthlyProcessReminderDraft = async ({
  processId,
  channel = "draft",
  subject = "",
  to = "",
  cc = [],
  phone = "",
  missingSteps = [],
  userEmail
}) => {
  const reminderRecord = {
    channel,
    subject,
    to,
    cc,
    phone,
    missingSteps,
    createdAt: new Date().toISOString(),
    createdBy: userEmail || ""
  };

  await updateDoc(doc(db, "monthlyProcesses", processId), {
    reminderDrafts: arrayUnion(reminderRecord),
    lastReminderDraftAt: serverTimestamp(),
    lastReminderDraftBy: userEmail || "",
    lastReminderDraftChannel: channel,
    lastReminderDraftSubject: subject,
    lastReminderDraftTo: to,
    lastReminderDraftCc: cc,
    lastReminderDraftPhone: phone,
    lastReminderDraftMissingSteps: missingSteps,
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const updateMonthlyProcessCompletion = async ({
  processId,
  steps,
  userEmail,
  status
}) => {
  if (status === "completed") {
    return completeMonthlyProcess({ processId, userEmail });
  }

  return updateMonthlyProcessStepProgress({
    processId,
    steps,
    userEmail,
    status
  });
};

export const completeMonthlyProcess = async ({ processId, userEmail }) => {
  await updateDoc(doc(db, "monthlyProcesses", processId), {
    status: "completed",
    owner: "TAMAMLANDI",
    completedAt: serverTimestamp(),
    completedBy: userEmail || "",
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });

  return { success: true };
};

export const archiveMonthlyProcess = async (processId) => {
  await updateDoc(doc(db, "monthlyProcesses", processId), {
    status: "archived",
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const deleteMonthlyProcess = async (processId) => {
  await deleteDoc(doc(db, "monthlyProcesses", processId));
};

export const updateMonthlyProcessRepeatSettings = async ({
  processId,
  autoRepeat,
  recurrence,
  userEmail
}) => {
  if (!processId) {
    throw new Error("Süreç ID bulunamadı.");
  }

  return updateDoc(doc(db, "monthlyProcesses", processId), {
    autoRepeat: Boolean(autoRepeat),
    recurrence: normalizeMonthlyRecurrence(recurrence),
    updatedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });
};

export const getProcessOwners = async (processProjectId = DEFAULT_PROCESS_PROJECT_ID) => {
  const q = query(collection(db, "processOwners"), orderBy("order", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .filter((item) =>
      processProjectId === DEFAULT_PROCESS_PROJECT_ID
        ? !item.processProjectId || item.processProjectId === processProjectId
        : item.processProjectId === processProjectId
    );
};

export const createProcessOwner = async (name, processProjectId = DEFAULT_PROCESS_PROJECT_ID) => {
  return addDoc(collection(db, "processOwners"), {
    name,
    processProjectId,
    isActive: true,
    order: Date.now(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateProcessOwner = async ({ ownerId, data }) => {
  return updateDoc(doc(db, "processOwners", ownerId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteProcessOwner = async (ownerId) => {
  return deleteDoc(doc(db, "processOwners", ownerId));
};

export const updateMonthlyProcessOwner = async ({ processId, owner }) => {
  await updateDoc(doc(db, "monthlyProcesses", processId), {
    owner,
    updatedAt: serverTimestamp()
  });

  return { success: true };
};

export const getMonthlyProcessTimeEntries = async (processId) => {
  if (!processId) return [];

  const q = query(
    collection(db, "processTimeEntries"),
    where("processId", "==", processId)
  );
  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docItem) => ({
      id: docItem.id,
      ...docItem.data()
    }))
    .sort((first, second) =>
      String(second.startedAtIso || "").localeCompare(String(first.startedAtIso || ""))
    );
};

export const startMonthlyProcessTimer = async ({
  processId,
  processTitle = "",
  userId = "",
  userEmail = "",
  userName = ""
}) => {
  if (!processId) {
    throw new Error("Görev ID bulunamadı.");
  }

  const nowIso = new Date().toISOString();

  const reference = await addDoc(collection(db, "processTimeEntries"), {
    processId,
    processTitle,
    userId,
    userEmail,
    userName,
    entryType: "timer",
    status: "running",
    isManual: false,
    startedAtIso: nowIso,
    endedAtIso: "",
    durationSeconds: 0,
    note: "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return { id: reference.id, startedAtIso: nowIso };
};

export const stopMonthlyProcessTimer = async ({
  entryId,
  startedAtIso,
  userEmail = ""
}) => {
  if (!entryId) {
    throw new Error("Zaman kaydı bulunamadı.");
  }

  const endDate = new Date();
  const startDate = new Date(startedAtIso);
  const durationSeconds = Number.isNaN(startDate.getTime())
    ? 0
    : Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 1000));

  await updateDoc(doc(db, "processTimeEntries", entryId), {
    status: "stopped",
    endedAtIso: endDate.toISOString(),
    durationSeconds,
    stoppedBy: userEmail || "",
    updatedAt: serverTimestamp()
  });

  return { durationSeconds };
};

export const addManualMonthlyProcessTimeEntry = async ({
  processId,
  processTitle = "",
  userId = "",
  userEmail = "",
  userName = "",
  startedAtIso,
  endedAtIso,
  durationSeconds,
  note = ""
}) => {
  if (!processId) {
    throw new Error("Görev ID bulunamadı.");
  }

  return addDoc(collection(db, "processTimeEntries"), {
    processId,
    processTitle,
    userId,
    userEmail,
    userName,
    entryType: "manual",
    status: "completed",
    isManual: true,
    startedAtIso,
    endedAtIso,
    durationSeconds,
    note,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const getCompanyContacts = async () => {
  const q = query(collection(db, "companyContacts"), orderBy("companyName", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data()
  }));
};

export const createCompanyContact = async ({
  companyName,
  companyShortName,
  contactName,
  title,
  phone,
  email,
  note
}) => {
  return addDoc(collection(db, "companyContacts"), {
    companyName,
    companyShortName: companyShortName || "",
    contactName,
    title: title || "",
    phone: phone || "",
    email: email || "",
    note: note || "",
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const deleteCompanyContact = async (contactId) => {
  return deleteDoc(doc(db, "companyContacts", contactId));
};

export const updateCompanyContact = async ({ contactId, data }) => {
  return updateDoc(doc(db, "companyContacts", contactId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const getTechnoparkContacts = async () => {
  const q = query(collection(db, "technoparkContacts"), orderBy("technoparkName", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data()
  }));
};

export const createTechnoparkContact = async ({
  technoparkName,
  contactName,
  title,
  phone,
  email,
  note,
  notes
}) => {
  const safeNotes = Array.isArray(notes)
    ? notes.map((item) => String(item || "").trim()).filter(Boolean)
    : (note ? [String(note).trim()] : []);

  return addDoc(collection(db, "technoparkContacts"), {
    technoparkName,
    contactName,
    title: title || "",
    phone: phone || "",
    email: email || "",
    note: note || "",
    notes: safeNotes,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateTechnoparkContact = async ({ contactId, data }) => {
  return updateDoc(doc(db, "technoparkContacts", contactId), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteTechnoparkContact = async (contactId) => {
  return deleteDoc(doc(db, "technoparkContacts", contactId));
};

export const getTechnoparkNotes = async () => {
  const q = query(collection(db, "technoparkNotes"), orderBy("technoparkName", "asc"));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((docItem) => ({
    id: docItem.id,
    ...docItem.data()
  }));
};

export const createTechnoparkNote = async ({
  technoparkName,
  note
}) => {
  return addDoc(collection(db, "technoparkNotes"), {
    technoparkName,
    note: String(note || "").trim(),
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const deleteTechnoparkNote = async (noteId) => {
  return deleteDoc(doc(db, "technoparkNotes", noteId));
};

export const createMonthlyProcessesBulk = async ({
  companies,
  month,
  template,
  owner,
  existingProcesses = [],
  processProjectId = DEFAULT_PROCESS_PROJECT_ID
}) => {
  const uniqueCompanies = [...new Set((companies || []).filter(Boolean))];
  const skippedCompanies = [];
  const createdCompanies = [];

  for (const companyName of uniqueCompanies) {
    const alreadyExists = existingProcesses.some(
      (process) =>
        process.companyName === companyName &&
        process.month === month &&
        process.templateId === template.id
    );

    if (alreadyExists) {
      skippedCompanies.push(companyName);
      continue;
    }

    await createMonthlyProcess({
      companyName,
      month,
      template,
      owner,
      processProjectId
    });

    createdCompanies.push(companyName);
  }

  return {
    createdCount: createdCompanies.length,
    skippedCount: skippedCompanies.length
  };
};

export const updateMonthlyProcessNote = async ({ processId, notes }) => {
  await updateDoc(doc(db, "monthlyProcesses", processId), {
    notes: notes || [],
    note: "",
    updatedAt: serverTimestamp()
  });

  return { success: true };
};
